import { ExternalLink } from 'lucide-react';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { PageHeader } from '../../components/common/PageHeader';
import { appRoutes } from '../../app-routes';
import { AutomationsTab } from '../feature-setup/automations-tab';
import { SegmentsTab } from '../feature-setup/segments-tab';
import { ReputationMetrics } from '../metrics/tabs/ReputationMetrics';
import styles from './ReputationNavigator.module.css';

type TabName = 'usage' | 'automations' | 'segments';

const VALID_TABS: TabName[] = ['usage', 'automations', 'segments'];

function getValidTab(tab: string | undefined): TabName {
  if (VALID_TABS.includes(tab as TabName)) return tab as TabName;
  return 'usage';
}

export const ReputationNavigator: React.FC = () => {
  const { tab = 'usage' } = useParams();
  const navigate = useNavigate();

  const activeTab = getValidTab(tab);
  useEffect(() => {
    if (!VALID_TABS.includes(tab as TabName)) {
      navigate(`${appRoutes.REPUTATION_SETUP}/usage`, { replace: true });
    }
  }, [tab, navigate]);

  const handleTabChange = (newTab: string) => {
    navigate(`${appRoutes.REPUTATION_SETUP}/${getValidTab(newTab)}`, { replace: true });
  };

  const components: Record<TabName, React.FC> = {
    usage: ReputationMetrics,
    automations: AutomationsTab,
    segments: SegmentsTab,
  };
  const TabComponent = components[activeTab];

  return (
    <section className={styles.page}>
      <PageHeader
        title="Reputation"
        description="Automated review requests and feedback linked to Google Business Profiles"
        right={
          <a
            href="https://docs.embedreach.com/reputation/introduction"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.docsLink}>
            API Docs
            <ExternalLink />
          </a>
        }
      />
      <Tabs value={activeTab} onValueChange={handleTabChange} className={styles.tabs}>
        <TabsList>
          <TabsTrigger value="usage">Usage</TabsTrigger>
          <TabsTrigger value="automations">Automations</TabsTrigger>
          <TabsTrigger value="segments">Segments</TabsTrigger>
        </TabsList>
        <TabComponent />
      </Tabs>
    </section>
  );
};
